import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './InvoicesSection.styles';
import { Invoice } from './Invoices.types';
import { useInvoiceListPayment, useInvoices } from './hooks';
import { getActiveStatusFilter } from './Invoices.helpers';
import { getInvoicePaymentDetails } from './hooks/useInvoices.helpers';
import { InvoicesTabs } from './components/InvoicesTabs';
import { InvoicesTable } from './components/InvoicesTable';
import { InvoicesTableSkeleton } from './components/InvoicesTableSkeleton';
import { InvoicesList } from './components/InvoicesList';
import { InvoicesListSkeleton } from './components/InvoicesListSkeleton';
import { InvoicesEmptyState } from './components/InvoicesEmptyState';
import { InvoiceDetailsModal } from './components/InvoiceDetailsModal';
import { InvoiceDetailsBottomSheet } from './components/InvoiceDetailsBottomSheet';
import { useResponsive } from '@/hooks/useResponsive';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { BusinessTabs } from '@/features/Profile/core';
import { BusinessTabName } from '@/features/Profile/core/BusinessTabs/BusinessTabs.types';
import { useFreezeProfileSidebarWhile } from '@/features/Profile/core/ProfileLayout/ProfileLayout.hooks';
import { InvoicePaymentModal } from '@/features/Profile/sections/Packages/components/InvoicePaymentModal';
import { InvoicePaymentSuccessModal } from '@/features/Profile/sections/Packages/components/InvoicePaymentSuccessModal';

export function InvoicesSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const activeStatus = getActiveStatusFilter(router.query);
  const {
    invoices,
    isLoading,
    isLoadingMore,
    hasNextPage,
    canLoadMore,
    loadMore,
  } = useInvoices(activeStatus, !router.isReady);
  const invoicePayment = useInvoiceListPayment({
    invoice: selectedInvoice,
    isOpen: isPaymentOpen,
    onClose: closePayment,
    onSuccess: handlePaymentSuccess,
  });
  useFreezeProfileSidebarWhile(isDesktop && isLoading);

  const isEmpty =
    !isLoading && !isLoadingMore && invoices.length === 0 && !hasNextPage;

  function handleShowDetails(invoice: Invoice) {
    setIsPaymentOpen(false);
    setSelectedInvoice(invoice);
  }

  function handleCloseDetails() {
    invoicePayment.controller.resetInvoice();
    setIsPaymentOpen(false);
    setSelectedInvoice(null);
  }

  function openPayment() {
    setIsPaymentOpen(true);
  }

  function closePayment() {
    setIsPaymentOpen(false);
  }

  function handlePaymentBack() {
    invoicePayment.controller.resetInvoice();
    closePayment();
  }

  function handlePaymentSuccess() {
    setSelectedInvoice(null);
  }

  return (
    <S.Box>
      {!isDesktop && <BusinessTabs activeTab={BusinessTabName.Invoices} />}

      <S.Container>
        <S.Title>{t('pages.cabinet.invoices')}</S.Title>
        <InvoicesTabs activeStatus={activeStatus} />

        <S.DesktopOnly>
          {isLoading ? (
            <InvoicesTableSkeleton />
          ) : isEmpty ? (
            <InvoicesEmptyState />
          ) : (
            <InvoicesTable
              invoices={invoices}
              onShowDetails={handleShowDetails}
              loadMoreSkeleton={
                isLoadingMore ? (
                  <InvoicesTableSkeleton
                    rowsCount={4}
                    hasHeader={false}
                    hasContainer={false}
                  />
                ) : null
              }
            />
          )}
        </S.DesktopOnly>

        <S.MobileOnly>
          {isLoading ? (
            <InvoicesListSkeleton />
          ) : isEmpty ? (
            <InvoicesEmptyState />
          ) : (
            <InvoicesList
              invoices={invoices}
              onShowDetails={handleShowDetails}
              loadMoreSkeleton={
                isLoadingMore ? (
                  <InvoicesListSkeleton itemsCount={4} hasContainer={false} />
                ) : null
              }
            />
          )}
        </S.MobileOnly>

        {!isLoading && !isLoadingMore && canLoadMore && (
          <InfiniteLoader onVisit={loadMore} />
        )}
      </S.Container>

      {isDesktop ? (
        <InvoiceDetailsModal
          invoice={isPaymentOpen ? null : selectedInvoice}
          onClose={handleCloseDetails}
          onPay={openPayment}
        />
      ) : (
        <InvoiceDetailsBottomSheet
          invoice={isPaymentOpen ? null : selectedInvoice}
          onClose={handleCloseDetails}
          onPay={openPayment}
        />
      )}

      {selectedInvoice && (
        <InvoicePaymentModal
          cashBalance={invoicePayment.cashBalance}
          controller={invoicePayment.controller}
          details={getInvoicePaymentDetails(selectedInvoice)}
          isLoading={invoicePayment.isLoading}
          isOpen={isPaymentOpen}
          onBack={handlePaymentBack}
          onClose={handleCloseDetails}
          onPay={invoicePayment.controller.payInvoice}
        />
      )}
      <InvoicePaymentSuccessModal />
    </S.Box>
  );
}
